import { useCallback } from 'react';

import { isReviewerRole } from 'src/data/user';
import { useEffectOnce } from 'src/lib/hooks';
import { useHistory } from 'src/lib/hooks/useHistory';
import * as ls from 'src/lib/localData';
import { usePopups } from 'src/lib/popups';
import { ROUTES } from 'src/routes';
import { useInviteHandlers, useInviteToken } from 'src/services';
import { useAccountsOnboarding } from 'src/services/useAccountsOnboarding';
import { useCurrentUser, useRefreshCurrentUser } from 'src/store';

import { InviteSubmitPopup } from './InviteSubmitPopup';

export const INVITE_ACCEPT_POPUP = Symbol('INVITE_ACCEPT');

export const useInviteAcceptPopup = () => {
  const popups = usePopups();
  const history = useHistory();

  useEffectOnce(() => {
    popups.registerPopup(INVITE_ACCEPT_POPUP, InviteSubmitPopup);
  });

  const currentUser = useCurrentUser();
  const { token, removeTokenFromUrl } = useInviteToken();
  const { handleTokenValidation, handleInvitationAccept, handleInvitationDecline } =
    useInviteHandlers(token);
  const { refreshCurrentUser } = useRefreshCurrentUser();
  const { startAccountOnboarding } = useAccountsOnboarding();

  const showInviteAcceptModalIfNeeded = useCallback(async () => {
    if (!token || !currentUser) return;

    const { email, errorType, invitedBy, role, projectId } = await handleTokenValidation();

    if (errorType || email !== currentUser.email) {
      removeTokenFromUrl();
      return;
    }

    const closePopup = () => {
      popups.closePopup(INVITE_ACCEPT_POPUP);
      removeTokenFromUrl();
    };

    popups.openPopup(INVITE_ACCEPT_POPUP, {
      onAccept: async () => {
        const { customerId } = await handleInvitationAccept();

        if (customerId) {
          // set new customerId, so it will used for selecting new Customer in CustomerStore.init() function
          // CustomerStore.init() function is called after user refreshing
          ls.currentCustomerId.write(customerId);

          const refreshedUser = await refreshCurrentUser();

          if (!refreshedUser) return;

          // A reviewer is invited to one project: no account onboarding, straight to the project.
          if (isReviewerRole(role) && projectId) {
            closePopup();
            history.replace(`${ROUTES.PROJECTS}/${projectId}`);
            return;
          }

          startAccountOnboarding();
        }

        closePopup();
      },
      onDecline: () => {
        handleInvitationDecline();
        closePopup();
      },
      invitedBy,
    });
  }, [
    currentUser,
    popups,
    token,
    removeTokenFromUrl,
    handleInvitationAccept,
    handleInvitationDecline,
    handleTokenValidation,
    startAccountOnboarding,
    refreshCurrentUser,
    history,
  ]);

  return showInviteAcceptModalIfNeeded;
};
